// Fixed src/services/contentful.js
import { createClient } from "contentful";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { dummySkills, dummyTechnologies } from "../data/dummyData";

const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
});

export const getProjects = async () => {
  try {
    const response = await client.getEntries({
      content_type: "project",
      order: "-sys.createdAt", // Show newest projects first
    });

    return response.items.map((item) => {
      const fields = item.fields;

      // Handle Rich Text description
      let description = "";
      if (fields.description) {
        if (typeof fields.description === "string") {
          // If it's already a string, use it directly
          description = fields.description;
        } else if (fields.description.nodeType) {
          // If it's a Rich Text object, convert to plain text
          description = documentToPlainTextString(fields.description);
        } else {
          // Fallback for other formats
          description = String(fields.description);
        }
      }

      return {
        id: item.sys.id,
        title: fields.title || "",
        description: description,
        image: fields.image?.fields?.file?.url
          ? `https:${fields.image.fields.file.url}`
          : null,
        technologies: fields.technologies || [],
        githubURL: fields.githubURL || "",
        liveURL: fields.liveURL || "",
        // Additional fields (add these to your Contentful model)
        category: fields.category || "Other",
        featured: fields.featured || false,
        status: fields.status || "Completed",
        displayOrder: fields.displayOrder || 999,
      };
    });
  } catch (error) {
    console.error("Error fetching projects:", error);
    return [];
  }
};

export const getExperiences = async () => {
  try {
    const response = await client.getEntries({
      content_type: "workExperience",
      order: "fields.displayOrder", // Order by display order (most recent first)
    });

    return response.items.map((item) => {
      const fields = item.fields;

      // Handle responsibilities - split by newlines if it's a string
      let responsibilities = [];
      if (fields.responsibilities) {
        responsibilities = fields.responsibilities
          .split("\n")
          .filter((item) => item.trim() !== "")
          .map((item) => item.trim());
      }

      // Handle achievements - split by newlines if it's a string
      let achievements = [];
      if (fields.achievements) {
        achievements = fields.achievements
          .split("\n")
          .filter((item) => item.trim() !== "")
          .map((item) => item.trim());
      }

      return {
        id: item.sys.id,
        jobTitle: fields.jobTitle || "",
        company: fields.companyName || fields.company || "",
        companyUrl: fields.companyUrl || "#",
        employmentType: fields.employmentType || "Full-time",
        location: fields.location || "",
        startDate: fields.startDate || "",
        endDate: fields.endDate || null,
        isCurrent: fields.isCurrent || false,
        description: fields.description || "",
        responsibilities: responsibilities,
        technologies: fields.technologies || [],
        achievements: achievements,
        companyLogo: fields.companyLogo?.fields?.file?.url
          ? `https:${fields.companyLogo.fields.file.url}`
          : "/images/companies/default-logo.png",
        displayOrder: fields.displayOrder || 999,
      };
    });
  } catch (error) {
    console.error("Error fetching experiences:", error);
    return [];
  }
};

// Add these functions to the end of your existing src/services/contentful.js

// Import dummy data at the top of your file
// import { dummySkills, dummyTechnologies } from '../data/dummyData';

// Fetch skills from Contentful
export const getSkills = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'skill',
      order: '-fields.proficiency', // Order by proficiency desc
    });

    if (response.items.length === 0) {
      console.log('No skills found in Contentful, using dummy data');
      return dummySkills;
    }

    return response.items.map(item => {
      const fields = item.fields;
      
      return {
        id: item.sys.id,
        name: fields.name || '',
        description: fields.description || '',
        icon: fields.icon?.fields?.file?.url ? `https:${fields.icon.fields.file.url}` : '🔧',
        proficiency: fields.proficiency || 0,
        category: fields.category || 'Other',
        createdAt: item.sys.createdAt,
        updatedAt: item.sys.updatedAt,
      };
    });
  } catch (error) {
    console.error('Error fetching skills from Contentful:', error);
    console.log('Falling back to dummy data');
    return dummySkills;
  }
};

// Fetch technologies from Contentful
export const getTechnologies = async () => {
  try {
    const response = await client.getEntries({
      content_type: 'technology',
      order: '-fields.proficiency', // Order by proficiency desc
    });

    if (response.items.length === 0) {
      console.log('No technologies found in Contentful, using dummy data');
      return dummyTechnologies;
    }

    return response.items.map(item => {
      const fields = item.fields;
      
      return {
        id: item.sys.id,
        name: fields.name || '',
        icon: fields.icon?.fields?.file?.url ? `https:${fields.icon.fields.file.url}` : '⚙️',
        category: fields.category || 'Other',
        proficiency: fields.proficiency || 0,
        color: fields.color || '#6B7280',
        yearsExp: fields.yearsExperience || 0,
        description: fields.description || '',
        createdAt: item.sys.createdAt,
        updatedAt: item.sys.updatedAt,
      };
    });
  } catch (error) {
    console.error('Error fetching technologies from Contentful:', error);
    console.log('Falling back to dummy data');
    return dummyTechnologies;
  }
};

// Fetch skills by category
export const getSkillsByCategory = async (category) => {
  try {
    const response = await client.getEntries({
      content_type: 'skill',
      'fields.category': category,
      order: '-fields.proficiency',
    });

    if (response.items.length === 0) {
      return dummySkills.filter(skill => skill.category === category);
    }

    return response.items.map(item => {
      const fields = item.fields;
      
      return {
        id: item.sys.id,
        name: fields.name || '',
        description: fields.description || '',
        icon: fields.icon?.fields?.file?.url ? `https:${fields.icon.fields.file.url}` : '🔧',
        proficiency: fields.proficiency || 0,
        category: fields.category || 'Other',
        createdAt: item.sys.createdAt,
        updatedAt: item.sys.updatedAt,
      };
    });
  } catch (error) {
    console.error(`Error fetching skills for category ${category}:`, error);
    return dummySkills.filter(skill => skill.category === category);
  }
};

// Fetch technologies by category
export const getTechnologiesByCategory = async (category) => {
  try {
    const response = await client.getEntries({
      content_type: 'technology',
      'fields.category': category,
      order: '-fields.proficiency',
    });

    if (response.items.length === 0) {
      return dummyTechnologies.filter(tech => tech.category === category);
    }

    return response.items.map(item => {
      const fields = item.fields;
      
      return {
        id: item.sys.id,
        name: fields.name || '',
        icon: fields.icon?.fields?.file?.url ? `https:${fields.icon.fields.file.url}` : '⚙️',
        category: fields.category || 'Other',
        proficiency: fields.proficiency || 0,
        color: fields.color || '#6B7280',
        yearsExp: fields.yearsExperience || 0,
        description: fields.description || '',
        createdAt: item.sys.createdAt,
        updatedAt: item.sys.updatedAt,
      };
    });
  } catch (error) {
    console.error(`Error fetching technologies for category ${category}:`, error);
    return dummyTechnologies.filter(tech => tech.category === category);
  }
};

// Fetch both skills and technologies
export const getSkillsAndTechnologies = async () => {
  try {
    const [skills, technologies] = await Promise.all([
      getSkills(),
      getTechnologies(),
    ]);

    return { skills, technologies };
  } catch (error) {
    console.error('Error fetching skills and technologies:', error);
    return { skills: dummySkills, technologies: dummyTechnologies };
  }
};